import { CollapsibleBox, CollapsibleBoxContainer } from '@/components/shared/CollapsibleBox';
import { adjustContentVisibility, Section, SectionParagraph } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { Box, Flex } from '@lemonade-hq/cdk';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import { COLORS } from '@lemonade-hq/tokens';
import parse from 'html-react-parser';
import type React from 'react';
import styled from 'styled-components';
import { SectionSubtitle, SectionTitlePet, SectionWrapperInner } from '../StyledElements';
import { type GoodToKnowInfoProps, getGoodToKnowInfo } from './GoodToKnowInfo';

interface StyledSectionProps {
  bg: 'default' | 'dark' | 'light';
}
const mapBgToColor: Record<StyledSectionProps['bg'], keyof typeof COLORS> = {
  default: 'glass',
  dark: 'cloud',
  light: 'white',
};

const StyledSection = styled(Section)<StyledSectionProps>`
  background-color: ${({ bg }: StyledSectionProps) => COLORS[mapBgToColor[bg]]}!important;
  ${adjustContentVisibility};
`;

const CTA = styled(JoinCTA)`
  padding-inline: 29px;
`;

interface GoodToKnowSectionProps extends React.ComponentProps<'div'>, Partial<Pick<StyledSectionProps, 'bg'>> {
  data?: GoodToKnowInfoProps[];
  title?: string;
  subtitle?: string;
}

const GoodToKnowSection: React.FC<GoodToKnowSectionProps> = ({
  bg = 'default',
  data = getGoodToKnowInfo,
  title = 'Got questions about pet insurance?',
  subtitle = `Here are a few important things to note about your pet health insurance plan. If you still have questions, you can check out <a style={{ display: 'inline-block' }} target="_blank" rel="noreferrer" href="https://www.lemonade.com/pet/explained/">pet insurance explained</a>.`,
  ...props
}) => {
  const { trackEvent } = useAnalyticsContext();

  const petFaqTrackEvent = ({ eventName, analytics }: { eventName: string; analytics: string }) =>
    trackEvent(eventName, {
      product: 'pet',
      analytics,
    });

  return (
    <StyledSection pt={[30, 60]} pb={[30, 100]} bg={bg} px={20} {...props}>
      <SectionWrapperInner justifyContent="center" alignItems={['normal', 'center']}>
        <SectionTitlePet tabIndex={0}>{title}</SectionTitlePet>
        <SectionSubtitle width={[1, 640]}>{parse(subtitle)}</SectionSubtitle>

        <CollapsibleBoxContainer minBoxesHeight="125px">
          {data.map(({ question, answer, analytics }) => (
            <CollapsibleBox
              key={question}
              title={question}
              content={answer}
              onMouseEnter={() =>
                petFaqTrackEvent({
                  eventName: 'faq.hovered',
                  analytics,
                })
              }
              onClick={() =>
                petFaqTrackEvent({
                  eventName: 'faq.clicked',
                  analytics,
                })
              }
            />
          ))}
        </CollapsibleBoxContainer>

        <Flex mt="40px" gap="30px" flexDirection="column" justifyContent="center" alignItems="center">
          <SectionParagraph>If you have more questions</SectionParagraph>
          <Box>
            <CTA
              aria-label="If you have more questions, visit our FAQs"
              size="small"
              flavor="primary"
              glowing
              customUrl="https://www.lemonade.com/pet/explained/lemonade-pet-insurance-faq/"
            >
              visit our FAQs
            </CTA>
          </Box>
        </Flex>
      </SectionWrapperInner>
    </StyledSection>
  );
};

export default GoodToKnowSection;
